import React, { useCallback, useMemo } from 'react';
import { IParagraphWidget, ITextBlock, ISuggestion } from './types';
import SubBlockEditor from './composite-text-widget/SubBlockEditor';

interface ParagraphWidgetProps {
    widget: IParagraphWidget;
    onWidgetChange: (newWidget: IParagraphWidget) => void;
    mode: 'editor' | 'reader';
    dossierId: string;
    onSuggestionCreated: (newSuggestion: ISuggestion) => void;
    mongoId: string;
    userRole: string | null;
    chapterIndex: number;
    chapterTitle: string;
}

const ParagraphWidget: React.FC<ParagraphWidgetProps> = ({
    widget,
    onWidgetChange,
    mode,
    dossierId,
    onSuggestionCreated,
    mongoId,
    userRole,
    chapterIndex,
    chapterTitle
}) => {
    // Mémoïser le handler de changement de bloc de texte
    const handleTextBlockChange = useCallback((index: number, newTextBlock: ITextBlock) => {
        const newBlocks = [...widget.content.blocks];
        newBlocks[index] = newTextBlock;
        onWidgetChange({
            ...widget,
            content: {
                ...widget.content,
                blocks: newBlocks,
            },
        });
    }, [widget, onWidgetChange]);

    // Mémoïser les classes CSS dynamiques
    const containerClasses = useMemo(() => {
        // 'widget-paragraph' sera utilisée pour le compteur CSS
        // 'pl-8' laisse de la place pour le numéro en absolute/marker
        const baseClasses = 'widget-paragraph relative pl-8 typo-justify-fr dark:text-mcdm-primary-100';
        const editorClasses = mode === 'editor' ? 'border border-solid border-transparent hover:border-[#f0f0f0]' : '';
        return `${baseClasses} ${editorClasses}`;
    }, [mode]);

    // Mémoïser le rendu des blocs
    const renderedBlocks = useMemo(() => {
        return widget.content.blocks.map((block, index) => (
            <SubBlockEditor
                key={`${block._id || index}`}
                textBlock={block}
                onTextBlockChange={(newTextBlock) => handleTextBlockChange(index, newTextBlock)}
                mode={mode}
                dossierId={dossierId}
                onSuggestionCreated={onSuggestionCreated}
                compositeBlockId={mongoId}
                userRole={userRole}
                chapterIndex={chapterIndex}
                chapterTitle={chapterTitle}
                isFirst={index === 0}
                placeholderClassName="pl-8"
            />
        ));
    }, [widget.content.blocks, handleTextBlockChange, mode, dossierId, onSuggestionCreated, mongoId, userRole, chapterIndex, chapterTitle]);

    return (
        <div
            className={containerClasses.replace('pl-8', '')}
            style={{ padding: '1px 0px 0px 0px' }} // Fallback padding explicit
        >
            {renderedBlocks}
        </div>
    );
};

export default React.memo(ParagraphWidget, (prevProps, nextProps) => {
    if (
        prevProps.mode !== nextProps.mode ||
        prevProps.dossierId !== nextProps.dossierId ||
        prevProps.mongoId !== nextProps.mongoId ||
        prevProps.userRole !== nextProps.userRole ||
        prevProps.chapterIndex !== nextProps.chapterIndex ||
        prevProps.chapterTitle !== nextProps.chapterTitle ||
        prevProps.onWidgetChange !== nextProps.onWidgetChange ||
        prevProps.onSuggestionCreated !== nextProps.onSuggestionCreated
    ) {
        return false;
    }

    if (JSON.stringify(prevProps.widget) !== JSON.stringify(nextProps.widget)) {
        return false;
    }

    return true;
});
